import { searchApiClient } from '@/datasources/search.datasource';
import {
  SearchApiBranch,
  SearchApiDayTime,
  SearchApiFacility,
  SearchApiItemResponse,
  SearchApiListResponse,
  SearchApiPartner,
  SearchApiPartnerProcedureGroup,
  SearchApiProcedure,
  SearchApiProcedureGroup,
  SearchApiProcedurePricing,
  SearchApiProfessional,
  SearchApiRating,
  SearchApiScheduleSlotGroup,
  SearchApiServiceChannel,
} from '@/app/(main)/(pages)/_types/search-api-response.type';

export async function findProceduresBySlugs(slugs: string[]): Promise<SearchApiProcedure[]> {
  const response = await searchApiClient.get<SearchApiListResponse<SearchApiProcedure>>(
    '/procedures/find-by-slugs',
    { slugs },
  );

  return response.data.data ?? [];
}

export interface FindSchedulesByProfessionalIdParams {
  serviceChannel?: SearchApiServiceChannel;
  partnerFacilityId?: string;
  page?: number;
  limit?: number;
}

export interface FindSchedulesByProfessionalIdResult {
  groups: SearchApiProcedureGroup[];
  hasMore: boolean;
}

export async function findSchedulesByProfessionalId(
  id: string,
  params: FindSchedulesByProfessionalIdParams = {},
): Promise<FindSchedulesByProfessionalIdResult> {
  const response = await searchApiClient.get<SearchApiListResponse<SearchApiProcedureGroup>>(
    `/schedules/find-by-professional-id/${id}`,
    { ...params },
  );

  return { groups: response.data.data ?? [], hasMore: response.data.hasMore ?? false };
}

export interface FindSchedulesByPartnerIdParams {
  serviceChannel?: SearchApiServiceChannel;
  partnerFacilityId?: string;
  page?: number;
  limit?: number;
}

export interface FindSchedulesByPartnerIdResult {
  groups: SearchApiPartnerProcedureGroup[];
  hasMore: boolean;
}

export async function findSchedulesByPartnerId(
  id: string,
  params: FindSchedulesByPartnerIdParams = {},
): Promise<FindSchedulesByPartnerIdResult> {
  const response = await searchApiClient.get<SearchApiListResponse<SearchApiPartnerProcedureGroup>>(
    `/schedules/find-by-partner-id/${id}`,
    { ...params },
  );

  return { groups: response.data.data ?? [], hasMore: response.data.hasMore ?? false };
}

export interface FindSlotsByScheduleIdsParams {
  limitDays: number;
  offsetDays: number;
  dayTime?: SearchApiDayTime;
  minDate?: string;
  maxDate?: string;
}

export interface FindSlotsByScheduleIdsResult {
  groups: SearchApiScheduleSlotGroup[];
  hasMore: boolean;
}

export async function findSlotsByScheduleIds(
  scheduleIds: string[],
  params: FindSlotsByScheduleIdsParams,
): Promise<FindSlotsByScheduleIdsResult> {
  const { dayTime, minDate, maxDate, ...rest } = params;

  const response = await searchApiClient.get<SearchApiListResponse<SearchApiScheduleSlotGroup>>(
    '/schedules/find-slots-by-schedule-ids',
    {
      scheduleIds,
      ...rest,
      ...(dayTime ? { dayTime } : {}),
      ...(minDate ? { minDate } : {}),
      ...(maxDate ? { maxDate } : {}),
    },
  );

  return { groups: response.data.data ?? [], hasMore: response.data.hasMore ?? false };
}

export async function findProfessionalById(id: string): Promise<SearchApiProfessional> {
  const response = await searchApiClient.get<SearchApiItemResponse<SearchApiProfessional>>(
    `/professionals/find-by-id/${id}`,
  );

  return response.data.data;
}

export async function findProfessionalRatingById(id: string): Promise<SearchApiRating> {
  const response = await searchApiClient.get<SearchApiItemResponse<SearchApiRating>>(
    `/professionals/find-rating-by-id/${id}`,
  );

  return response.data.data;
}

export async function findPartnerById(id: string): Promise<SearchApiPartner> {
  const response = await searchApiClient.get<SearchApiItemResponse<SearchApiPartner>>(
    `/partners/find-by-id/${id}`,
  );

  return response.data.data;
}

export async function findFacilityById(id: string): Promise<SearchApiFacility> {
  const response = await searchApiClient.get<SearchApiItemResponse<SearchApiFacility>>(
    `/partners/facilities/find-by-id/${id}`,
  );

  return response.data.data;
}

export interface FindBranchesByLocationParams {
  city?: string;
  latitude?: number;
  longitude?: number;
  page?: number;
  limit?: number;
}

export interface FindBranchesByLocationResult {
  branches: SearchApiBranch[];
  hasMore: boolean;
}

export async function findBranchesByLocation(
  params: FindBranchesByLocationParams,
): Promise<FindBranchesByLocationResult> {
  const response = await searchApiClient.get<SearchApiListResponse<SearchApiBranch>>(
    '/branches/find-by-location',
    { ...params },
  );

  return { branches: response.data.data ?? [], hasMore: response.data.hasMore ?? false };
}

export async function findProceduresPricing(
  partnerFacilityId: string,
  procedureIds: string[],
): Promise<SearchApiProcedurePricing | null> {
  const response = await searchApiClient.get<SearchApiListResponse<SearchApiProcedurePricing>>(
    '/partners/facilities/find-procedures-pricing',
    { partnerFacilityIds: partnerFacilityId, procedureIds },
  );

  return response.data.data?.[0] ?? null;
}
